import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import ExpenseList from "@/components/ExpenseList";
import AddExpenseDialog from "@/components/AddExpenseDialog";
import Sidebar from "@/components/Sidebar";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import { getExchangeRate } from "@/utils/exchangeRates";
import CategoryManager from "@/components/CategoryManager";
import Header from "@/components/Header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useHeaderData } from "@/hooks/use-header-data";

const ExpenseListPage = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [exchangeRates, setExchangeRates] = useState<{ [key: string]: number }>({});
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [localCurrency, setLocalCurrency] = useState<string>('USD');
  const { organization, organizations } = useHeaderData();

  // Set default date range to current month
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1);
    return format(date, 'yyyy-MM-dd');
  });
  
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    date.setDate(0);
    return format(date, 'yyyy-MM-dd');
  });

  // Add state for date picker UI
  const [startDateObj, setStartDateObj] = useState<Date>(() => {
    const date = new Date();
    date.setDate(1);
    return date;
  });
  
  const [endDateObj, setEndDateObj] = useState<Date>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    date.setDate(0);
    return date;
  });

  // Add state for iOS-style date picker sheets
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());
  const [activeDatePicker, setActiveDatePicker] = useState<'start' | 'end' | null>(null);

  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  // Arrays for picker values
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  
  // Generate arrays of days (1-31) and years (current year - 5 to current year + 5)
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const years = Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 5 + i);

  // Authentication effect
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
        return;
      }
      setUser(session.user);
    };

    checkAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        navigate("/login");
        return;
      }
      setUser(session.user);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleDateChange = (type: 'start' | 'end', date: Date | undefined) => {
    if (!date) return;
    
    if (type === 'start') {
      if (endDateObj && date > endDateObj) {
        toast({
          title: "Invalid Date Range",
          description: "Start date cannot be later than end date",
          variant: "destructive",
        });
        return;
      }
      setStartDateObj(date);
      setStartDate(format(date, 'yyyy-MM-dd'));
    } else {
      if (startDateObj && date < startDateObj) {
        toast({
          title: "Invalid Date Range",
          description: "End date cannot be earlier than start date",
          variant: "destructive",
        });
        return;
      }
      setEndDateObj(date);
      setEndDate(format(date, 'yyyy-MM-dd'));
    }
  };

  // Function to open iOS-style date picker
  const openDatePicker = (type: 'start' | 'end') => {
    setActiveDatePicker(type);
    const dateObj = type === 'start' ? startDateObj : endDateObj;
    setSelectedMonth(dateObj.getMonth());
    setSelectedDay(dateObj.getDate());
    setSelectedYear(dateObj.getFullYear());
    
    if (type === 'start') {
      setStartDatePickerOpen(true);
    } else {
      setEndDatePickerOpen(true);
    }
  };

  // Function to apply selected date from iOS picker
  const applySelectedDate = () => {
    const newDate = new Date(selectedYear, selectedMonth, selectedDay);
    
    if (activeDatePicker === 'start') {
      handleDateChange('start', newDate);
      setStartDatePickerOpen(false);
    } else if (activeDatePicker === 'end') {
      handleDateChange('end', newDate);
      setEndDatePickerOpen(false);
    }
    
    setActiveDatePicker(null);
  };

  // Get categories for the filter dropdown
  const { data: categories } = useQuery({
    queryKey: ["categories", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .eq("organization_id", organizationId);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!organizationId,
  });

  // Fetch expense reports first to ensure we have all the report data
  const { data: expenseReports, refetch: refetchExpenseReports } = useQuery({
    queryKey: ["expense-reports", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      
      const { data, error } = await supabase
        .from("expense_reports")
        .select(`
          id, 
          status, 
          created_at, 
          date_from, 
          date_to, 
          generated_by, 
          total_amount,
          currency
        `)
        .eq("organization_id", organizationId);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!organizationId,
  });

  // Fetch expense report items to link with expenses
  const { data: expenseReportItems, refetch: refetchReportItems } = useQuery({
    queryKey: ["expense-report-items", organizationId, startDate, endDate, selectedCategory],
    queryFn: async () => {
      if (!organizationId) return [];
      
      let query = supabase
        .from("expense_report_items")
        .select(`
          id,
          expense_report_id,
          description,
          amount,
          date,
          currency,
          category_id,
          categories (
            id,
            name,
            color,
            organization_id
          )
        `)
        .eq("organization_id", organizationId);
      
      if (startDate) {
        query = query.gte("date", startDate);
      }
      
      if (endDate) {
        query = query.lte("date", endDate);
      }
      
      if (selectedCategory && selectedCategory !== "all") {
        query = query.eq("category_id", parseInt(selectedCategory));
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error("Error fetching expense report items:", error);
        throw error;
      }
      
      console.log("Fetched expense report items:", data?.length || 0);
      return data || [];
    },
    enabled: !!organizationId,
  });

  // Setting up a subscription to listen for changes in expense_reports
  useEffect(() => {
    if (!organizationId) return;
    
    const channel = supabase
      .channel('expense-reports-changes')
      .on('postgres_changes', 
        {
          event: '*',
          schema: 'public',
          table: 'expense_reports',
          filter: `organization_id=eq.${organizationId}`
        }, 
        () => {
          console.log('Expense report changed, refreshing data...');
          refetchExpenseReports();
          refetchReportItems();
          queryClient.invalidateQueries({ queryKey: ["expenses"] });
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [organizationId, refetchExpenseReports, refetchReportItems, queryClient]);

  // Updated expenses query to include expense report references
  const { data: expenses, refetch: refetchExpenses } = useQuery({
    queryKey: ["expenses", organizationId, startDate, endDate, selectedCategory, expenseReports, expenseReportItems],
    queryFn: async () => {
      if (!organizationId) return [];
      
      console.log('Fetching expenses with params:', {
        organizationId,
        startDate,
        endDate,
        selectedCategory
      });
      
      // Now get the regular expenses
      let query = supabase
        .from("expenses")
        .select(`
          *,
          profiles (
            id,
            first_name,
            last_name,
            avatar_url,
            updated_at
          ),
          categories (
            id,
            name,
            color,
            organization_id
          )
        `)
        .eq("organization_id", organizationId);

      if (startDate) {
        query = query.gte("date", startDate);
      }
      if (endDate) {
        query = query.lte("date", endDate);
      }
      if (selectedCategory && selectedCategory !== "all") {
        query = query.eq("category_id", parseInt(selectedCategory));
      }

      const { data: expensesData, error } = await query;
      
      if (error) {
        console.error("Error fetching expenses:", error);
        throw error;
      }
      
      // Add expense report information to expenses
      const processedExpenses = expensesData?.map(expense => {
        // Create a copy of the expense that we can safely modify
        const expenseWithReport = { ...expense, expense_report: null };
        
        // Find matching report item by comparing key fields
        if (expenseReportItems && expenseReports) {
          const matchingReportItem = expenseReportItems.find(item => 
            item.description === expense.description && 
            Number(item.amount) === Number(expense.amount) &&
            item.date === expense.date &&
            item.category_id === expense.category_id
          );
          
          if (matchingReportItem && matchingReportItem.expense_report_id) {
            const matchingReport = expenseReports.find(r => 
              r.id === matchingReportItem.expense_report_id
            );
            
            if (matchingReport) {
              expenseWithReport.expense_report = {
                id: matchingReport.id,
                status: matchingReport.status
              };
            }
          }
        }
        
        return expenseWithReport;
      });
      
      // Create report summary expenses for approved reports within our date range
      let reportSummaries: any[] = [];
      if (expenseReports) {
        const approvedReports = expenseReports.filter(r => r.status === 'approved');
        
        if (approvedReports.length > 0) {
          console.log("Creating summaries for approved reports:", approvedReports.length);
          
          // Filter report summaries based on selected category
          reportSummaries = approvedReports
            .filter(report => {
              // Only include reports that match the date filter
              const reportStartDate = report.date_from;
              return (!startDate || reportStartDate >= startDate) && 
                     (!endDate || reportStartDate <= endDate);
            })
            .map(report => {
              // For category filter, we need to check if any report items match the category
              if (selectedCategory && selectedCategory !== "all") {
                // Get report items for this report that match the category
                const reportItemsWithCategory = expenseReportItems?.filter(item => 
                  item.expense_report_id === report.id && 
                  item.category_id === parseInt(selectedCategory)
                );
                
                // If we have no matching items in this category, don't create a summary
                if (!reportItemsWithCategory || reportItemsWithCategory.length === 0) {
                  return null;
                }
                
                // Calculate sum of matching category items
                const categoryTotal = reportItemsWithCategory.reduce((sum, item) => 
                  sum + Number(item.amount), 0
                );
                
                // Create a category-specific summary
                return {
                  id: parseInt(`8${report.id.substring(0, 8)}`, 16),
                  organization_id: organizationId,
                  user_id: report.generated_by,
                  amount: Number(categoryTotal),
                  date: report.date_from,
                  created_at: report.created_at,
                  description: `Expense Report #${report.id.substring(0, 8)} (Summary)`,
                  currency: report.currency || 'USD',
                  category_id: parseInt(selectedCategory),
                  categories: {
                    id: parseInt(selectedCategory),
                    name: categories?.find(c => c.id === parseInt(selectedCategory))?.name || "Filtered Category",
                    color: "#4A7AFF",
                    organization_id: organizationId
                  },
                  base_currency: null,
                  exchange_rate: null,
                  remarks: `This is a summary entry for the expense report covering ${report.date_from} to ${report.date_to}`,
                  expense_report: {
                    id: report.id,
                    status: "approved"
                  },
                  is_report_summary: true
                };
              } else {
                // Normal summary for all categories
                return {
                  id: parseInt(`8${report.id.substring(0, 8)}`, 16),
                  organization_id: organizationId,
                  user_id: report.generated_by,
                  amount: Number(report.total_amount),
                  date: report.date_from,
                  created_at: report.created_at,
                  description: `Expense Report #${report.id.substring(0, 8)} (Summary)`,
                  currency: report.currency || 'USD',
                  category_id: null,
                  categories: {
                    id: 0,
                    name: "Expense Report",
                    color: "#4A7AFF",
                    organization_id: organizationId
                  },
                  base_currency: null,
                  exchange_rate: null,
                  remarks: `This is a summary entry for the expense report covering ${report.date_from} to ${report.date_to}`,
                  expense_report: {
                    id: report.id,
                    status: "approved"
                  },
                  is_report_summary: true
                };
              }
            }).filter(Boolean); // Remove any null entries
          
          console.log("Created report summaries:", reportSummaries.length);
        }
      }
      
      // Combine regular expenses with report summaries
      const allExpenses = [...(processedExpenses || []), ...reportSummaries];
      console.log("Processed expenses:", allExpenses.length);
      
      return allExpenses || [];
    },
    enabled: !!organizationId && !!expenseReportItems && !!expenseReports && !!categories,
  });

  useEffect(() => {
    if (organization?.currency) {
      console.log('Setting local currency from organization:', organization.currency);
      setLocalCurrency(organization.currency);
    }
  }, [organization]);

  // Add logging to exchange rates effect
  useEffect(() => {
    const fetchExchangeRates = async () => {
      if (!expenses || expenses.length === 0) return;
      
      const rates: { [key: string]: number } = {};
      const currencies = [...new Set(expenses.map(e => e.currency))];
      
      console.log('ExpenseList: Currencies found in expenses:', currencies);
      console.log('ExpenseList: Local currency is:', localCurrency);
      
      for (const currency of currencies) {
        if (currency !== localCurrency) {
          try {
            const rate = await getExchangeRate(currency, localCurrency);
            console.log(`ExpenseList: Got rate for ${currency} to ${localCurrency}: ${rate}`);
            rates[currency] = rate;
          } catch (error) {
            console.error(`ExpenseList: Error getting rate for ${currency}:`, error);
            rates[currency] = 1;
          }
        }
      }
      
      console.log('ExpenseList: Final exchange rates:', rates);
      setExchangeRates(rates);
    };

    if (expenses?.length > 0) {
      console.log('ExpenseList: Fetching exchange rates for expenses:', expenses);
      fetchExchangeRates();
    }
  }, [expenses, localCurrency]);

  useEffect(() => {
    if (!expenses || !exchangeRates) return;

    console.log('Calculating total with expenses:', expenses);
    console.log('Using exchange rates:', exchangeRates);
    console.log('Local currency:', localCurrency);

    const total = expenses.reduce((sum, expense) => {
      const amount = Number(expense.amount);
      
      if (expense.currency === localCurrency) {
        return sum + amount;
      }
      
      const rate = exchangeRates[expense.currency] || 1;
      const convertedAmount = amount * rate;
      return sum + convertedAmount;
    }, 0);

    console.log(`Final total in ${localCurrency}:`, total);
    setTotalAmount(total);
  }, [expenses, exchangeRates, localCurrency]);

  // Function to handle report status changes
  const handleReportStatusChange = () => {
    console.log("Report status changed, refreshing expenses and reports...");
    refetchExpenseReports();
    refetchReportItems();
    refetchExpenses();
  };

  // Add a useEffect to refetch data when returning to this page
  useEffect(() => {
    // Refetch data when the component mounts or when the route changes to this page
    const refetchAllData = async () => {
      await refetchExpenseReports();
      await refetchReportItems();
      await refetchExpenses();
    };
    
    refetchAllData();
  }, [organizationId, refetchExpenseReports, refetchReportItems, refetchExpenses]);

  // Now we can safely return null if no user
  if (!user) {
    return null;
  }

  // Remove duplicate categories
  const uniqueCategories = categories?.reduce((acc: any[], current) => {
    const isDuplicate = acc.find((item) => item.name === current.name);
    if (!isDuplicate) {
      acc.push(current);
    }
    return acc;
  }, []) || [];

  // Generate a consistent color based on organization name
  const getOrganizationColor = (name: string) => {
    const colors = [
      'bg-[#9b87f5]', // Primary Purple
      'bg-[#7E69AB]', // Secondary Purple
      'bg-[#0EA5E9]', // Ocean Blue
      'bg-[#1EAEDB]', // Bright Blue
      'bg-[#6E59A5]', // Tertiary Purple
    ];
    
    // Use string hash to pick a consistent color
    const hash = name.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    
    return colors[Math.abs(hash) % colors.length];
  };

  const handleExpenseAdded = () => {
    refetchExpenses();
  };

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={user}
        organization={organization}
        organizations={organizations}
      />

      <div className="flex min-h-[calc(100vh-4rem)]">
        <Sidebar className="border-r border-border/50" />
        <main className={cn(
          "flex-1 container mx-auto px-4 py-8 space-y-8",
          "relative z-10 bg-background"
        )}>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <div className="flex items-center gap-2">
                <div className="text-xl font-bold text-black">
                  Total: {localCurrency === 'USD' ? '$' : ''}{totalAmount.toFixed(2)} {localCurrency}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {organizationId && (
                  <>
                    <CategoryManager organizationId={organizationId} />
                    <AddExpenseDialog 
                      organizationId={organizationId}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Start Date</label>
                <Button
                  variant="outline"
                  className="w-full justify-between"
                  onClick={() => openDatePicker('start')}
                >
                  <span>{format(startDateObj, "MMM d, yyyy")}</span>
                  <CalendarIcon className="ml-2 h-4 w-4" />
                </Button>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">End Date</label>
                <Button
                  variant="outline"
                  className="w-full justify-between"
                  onClick={() => openDatePicker('end')}
                >
                  <span>{format(endDateObj, "MMM d, yyyy")}</span>
                  <CalendarIcon className="ml-2 h-4 w-4" />
                </Button>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Category</label>
                <Select
                  value={selectedCategory}
                  onValueChange={setSelectedCategory}
                >
                  <SelectTrigger className="ios-input">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {uniqueCategories?.map((category) => (
                      <SelectItem key={category.id} value={String(category.id)}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <ExpenseList expenses={expenses || []} onReportStatusChange={handleReportStatusChange} />
        </main>
      </div>

      {/* iOS-Style Date Picker Sheet for Start Date */}
      <Sheet open={startDatePickerOpen} onOpenChange={setStartDatePickerOpen}>
        <SheetContent side="bottom" className="py-0 px-0 max-h-[90vh]">
          <div className="ios-date-picker">
            <div className="ios-date-picker-header border-b p-4 flex justify-between">
              <button 
                onClick={() => setStartDatePickerOpen(false)}
                className="text-gray-500 font-medium"
              >
                Cancel
              </button>
              <h3 className="text-lg font-semibold">FROM</h3>
              <button 
                onClick={applySelectedDate}
                className="text-blue-500 font-medium"
              >
                Set
              </button>
            </div>
            
            <div className="ios-date-picker-wheels p-4 flex justify-center">
              <div className="ios-date-picker-wheel flex-1 overflow-auto text-center">
                {months.map((month, index) => (
                  <div 
                    key={month} 
                    className={`py-2 ${selectedMonth === index ? 'text-black font-semibold text-lg bg-yellow-200' : 'text-gray-400'}`}
                    onClick={() => setSelectedMonth(index)}
                  >
                    {month}
                  </div>
                ))}
              </div>
              
              <div className="ios-date-picker-wheel flex-1 overflow-auto text-center">
                {days.map((day) => (
                  <div 
                    key={day} 
                    className={`py-2 ${selectedDay === day ? 'text-black font-semibold text-lg bg-yellow-200' : 'text-gray-400'}`}
                    onClick={() => setSelectedDay(day)}
                  >
                    {day}
                  </div>
                ))}
              </div>
              
              <div className="ios-date-picker-wheel flex-1 overflow-auto text-center">
                {years.map((year) => (
                  <div 
                    key={year} 
                    className={`py-2 ${selectedYear === year ? 'text-black font-semibold text-lg bg-yellow-200' : 'text-gray-400'}`}
                    onClick={() => setSelectedYear(year)}
                  >
                    {year}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* iOS-Style Date Picker Sheet for End Date */}
      <Sheet open={endDatePickerOpen} onOpenChange={setEndDatePickerOpen}>
        <SheetContent side="bottom" className="py-0 px-0 max-h-[90vh]">
          <div className="ios-date-picker">
            <div className="ios-date-picker-header border-b p-4 flex justify-between">
              <button 
                onClick={() => setEndDatePickerOpen(false)}
                className="text-gray-500 font-medium"
              >
                Cancel
              </button>
              <h3 className="text-lg font-semibold">TO</h3>
              <button 
                onClick={applySelectedDate}
                className="text-blue-500 font-medium"
              >
                Set
              </button>
            </div>
            
            <div className="ios-date-picker-wheels p-4 flex justify-center">
              <div className="ios-date-picker-wheel flex-1 overflow-auto text-center">
                {months.map((month, index) => (
                  <div 
                    key={month} 
                    className={`py-2 ${selectedMonth === index ? 'text-black font-semibold text-lg bg-yellow-200' : 'text-gray-400'}`}
                    onClick={() => setSelectedMonth(index)}
                  >
                    {month}
                  </div>
                ))}
              </div>
              
              <div className="ios-date-picker-wheel flex-1 overflow-auto text-center">
                {days.map((day) => (
                  <div 
                    key={day} 
                    className={`py-2 ${selectedDay === day ? 'text-black font-semibold text-lg bg-yellow-200' : 'text-gray-400'}`}
                    onClick={() => setSelectedDay(day)}
                  >
                    {day}
                  </div>
                ))}
              </div>
              
              <div className="ios-date-picker-wheel flex-1 overflow-auto text-center">
                {years.map((year) => (
                  <div 
                    key={year} 
                    className={`py-2 ${selectedYear === year ? 'text-black font-semibold text-lg bg-yellow-200' : 'text-gray-400'}`}
                    onClick={() => setSelectedYear(year)}
                  >
                    {year}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ExpenseListPage;
